import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { AlertCircle, ArrowLeft, ArrowRight, User, Building2, Users } from 'lucide-react';
import { Logo, LogoWhite } from '@/components/Logo';
import { PasswordStrength } from '@/components/PasswordStrength';
import { AccountTypeSelector } from '@/components/register/AccountTypeSelector';
import { Seo } from '@/components/Seo';
import api from '@/lib/api';
import logger from '@/lib/logger';
import axios from 'axios';

const PUBLIC_API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function RegisterPage() {
  // Read seat preset (set by the marketing SeatCalculator) once at mount so we
  // can pre-select account type and skip the "Single vs Organization" picker.
  const seatPreset = (() => {
    try {
      const raw = localStorage.getItem('seatPreset');
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      const seats = Math.max(1, Math.min(parseInt(parsed.seats, 10) || 0, 499));
      if (!seats) return null;
      return { seats, cycle: parsed.cycle === 'annual' ? 'annual' : 'monthly' };
    } catch { return null; }
  })();

  const presetType = seatPreset ? (seatPreset.seats >= 2 ? 'organization' : 'single') : null;
  const presetMemberCount = seatPreset && seatPreset.seats >= 2 ? seatPreset.seats - 1 : 3;

  const [accountType, setAccountType] = useState(presetType); // null | 'single' | 'organization'
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [company, setCompany] = useState('');
  const [phone, setPhone] = useState('');
  // A2P 10DLC: explicit unchecked opt-in checkbox. Required when phone is given.
  const [smsConsent, setSmsConsent] = useState(false);
  const [orgName, setOrgName] = useState('');
  const [memberCount, setMemberCount] = useState(presetMemberCount);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(presetType ? 'form' : 'type'); // 'type' | 'form' | 'verify'
  const [code, setCode] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [resending, setResending] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Resume-from-email-quote: if `?resume=<token>` is present, fetch the
  // saved seats/cycle/email and pre-fill the form so the user can finish
  // the sign-up in one click. (Backed by POST /marketing/save-quote.)
  useEffect(() => {
    const token = searchParams.get('resume');
    if (!token) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${PUBLIC_API}/marketing/quote/${encodeURIComponent(token)}`);
        if (cancelled || !data) return;
        if (data.email) setEmail(data.email);
        if (data.seats && data.cycle) {
          try {
            localStorage.setItem('seatPreset', JSON.stringify({ seats: data.seats, cycle: data.cycle }));
          } catch { /* ignore quota */ }
          const seats = parseInt(data.seats, 10) || 1;
          if (seats >= 2) {
            setAccountType('organization');
            setMemberCount(Math.max(1, seats - 1));
          } else {
            setAccountType('single');
          }
          setStep('form');
        }
      } catch (err) {
        logger.error(err);
      }
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (user) {
      let target = '/dashboard';
      try {
        const raw = localStorage.getItem('seatPreset');
        if (raw) {
          const { seats, cycle } = JSON.parse(raw);
          if (seats) target = `/billing?seats=${seats}&cycle=${cycle || 'monthly'}`;
        }
      } catch { /* ignore parse errors */ }
      navigate(target);
    }
  }, [user, navigate]);

  const handleSelectType = (type) => {
    setAccountType(type);
    setStep('form');
    setError('');
  };

  const handleSubmitForm = async (e) => {
    e.preventDefault();
    setError('');
    if (!name.trim()) { setError('Name is required'); return; }
    if (!email.trim()) { setError('Email is required'); return; }
    if (password.length < 6) { setError('Password must be at least 6 characters'); return; }
    if (accountType === 'organization' && !orgName.trim()) { setError('Organization name is required'); return; }
    // A2P 10DLC: phone is optional, but if given, explicit SMS consent must be checked.
    if (phone.trim() && !smsConsent) {
      setError('Please check the SMS consent box to agree to receive text messages, or remove your phone number to skip SMS.');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        name: name.trim(),
        email: email.trim(),
        password,
        company,
        phone,
        account_type: accountType,
        organization_name: accountType === 'organization' ? orgName.trim() : '',
        max_seats: accountType === 'organization' ? memberCount + 1 : undefined, // +1 for admin seat
      };
      await api.sendVerification(payload);
      setStep('verify');
    } catch (e) {
      const detail = e.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Something went wrong');
    }
    setLoading(false);
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setError('');
    if (code.length !== 6) { setError('Please enter the 6-digit code'); return; }
    setVerifying(true);
    try {
      const { data } = await api.verifyEmail(email.trim(), code.trim());
      if (data.token) sessionStorage.setItem('_at', data.token);
      window.location.href = '/onboarding';
    } catch (e) {
      const detail = e.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Invalid code');
    }
    setVerifying(false);
  };

  const handleResend = async () => {
    setResending(true);
    setError('');
    try {
      await api.sendVerification({
        name: name.trim(), email: email.trim(), password, company, phone,
        account_type: accountType,
        organization_name: accountType === 'organization' ? orgName.trim() : '',
        max_seats: accountType === 'organization' ? memberCount + 1 : undefined,
      });
    } catch (err) { logger.error(err); }
    setResending(false);
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex" data-testid="register-page">
      <Seo
        title="Start Free Trial — Real Leads CRM"
        description="Create your Real Leads CRM account in 60 seconds. Share leads with your team, automate follow-ups, and track every email. $35/month."
        path="/register"
      />
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <img src="https://customer-assets.emergentagent.com/job_crm-collab-1/artifacts/mw2milqi_customer.jpg" alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-[#0047AB]/70" />
        <div className="relative flex items-end p-12 w-full">
          <div className="max-w-md">
            <LogoWhite size="lg" showText={false} className="mb-6" />
            <h2 className="font-heading text-3xl font-bold text-white tracking-tight mb-3">Start your free trial</h2>
            <p className="text-blue-200 text-sm leading-relaxed">7 days of full access. No credit card required. Share leads and close deals together.</p>
          </div>
        </div>
      </div>
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-sm">
          <div className="lg:hidden mb-8"><Logo size="sm" /></div>

          {/* ── STEP 1: Account Type Selection ── */}
          {step === 'type' && (
            <>
              <h1 className="font-heading text-2xl font-bold text-slate-900 tracking-tight mb-1">Create account</h1>
              <p className="text-sm text-slate-500 mb-6">What type of account do you need?</p>

              <AccountTypeSelector onSelectType={handleSelectType} />

              <p className="text-sm text-slate-500 text-center mt-6">
                Already have an account? <Link to="/login" className="text-[#0047AB] font-medium hover:underline" data-testid="register-login-link">Sign in</Link>
              </p>
            </>
          )}

          {/* ── STEP 2: Registration Form ── */}
          {step === 'form' && (
            <>
              {seatPreset ? (
                <Link
                  to={`/pricing?seats=${seatPreset.seats}&cycle=${seatPreset.cycle}`}
                  className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 mb-4 transition-colors"
                  data-testid="back-to-pricing-link"
                >
                  <ArrowLeft className="w-4 h-4" /> Back to pricing
                </Link>
              ) : (
                <button
                  onClick={() => { setStep('type'); setError(''); }}
                  className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 mb-4 transition-colors"
                  data-testid="back-to-type-btn"
                >
                  <ArrowLeft className="w-4 h-4" /> Back
                </button>
              )}

              {seatPreset && (
                <div
                  className="bg-blue-50 border border-blue-200 rounded-sm p-3 mb-5 flex items-center justify-between gap-3"
                  data-testid="register-plan-confirm"
                >
                  <div>
                    <p className="text-[10px] font-medium uppercase tracking-wide text-blue-600">
                      Selected Plan
                    </p>
                    <p className="text-sm font-semibold text-blue-900 mt-0.5">
                      {seatPreset.seats} seat{seatPreset.seats === 1 ? '' : 's'}
                      {' \u00b7 '}
                      {seatPreset.cycle === 'annual' ? 'Annual billing' : 'Monthly billing'}
                    </p>
                  </div>
                  <Link
                    to={`/pricing?seats=${seatPreset.seats}&cycle=${seatPreset.cycle}`}
                    className="text-[11px] text-blue-700 underline hover:text-blue-900"
                    data-testid="register-plan-change-link"
                  >
                    Change
                  </Link>
                </div>
              )}

              <div className="flex items-center gap-2 mb-1">
                {accountType === 'single' ? (
                  <User className="w-5 h-5 text-[#0047AB]" />
                ) : (
                  <Building2 className="w-5 h-5 text-[#0047AB]" />
                )}
                <h1 className="font-heading text-2xl font-bold text-slate-900 tracking-tight">
                  {accountType === 'single' ? 'Single User' : 'Organization'} Setup
                </h1>
              </div>
              <p className="text-sm text-slate-500 mb-5">
                {accountType === 'single'
                  ? 'Set up your personal admin account'
                  : 'Set up the admin account for your organization'}
              </p>

              {error && (
                <div className="flex items-center gap-2 p-3 mb-4 bg-red-50 border border-red-200 rounded-sm text-sm text-red-700" data-testid="register-error">
                  <AlertCircle className="w-4 h-4 shrink-0" /> {error}
                </div>
              )}

              <form onSubmit={handleSubmitForm} className="space-y-4">
                {accountType === 'organization' && (
                  <div>
                    <Label className="text-xs font-medium text-slate-700 uppercase tracking-wide">Organization Name *</Label>
                    <Input
                      value={orgName}
                      onChange={e => setOrgName(e.target.value)}
                      placeholder="Your company or team name"
                      className="mt-1 rounded-sm"
                      data-testid="register-org-name-input"
                    />
                  </div>
                )}

                {accountType === 'organization' && (
                  <div className="bg-blue-50 border border-blue-200 rounded-sm p-3">
                    <p className="text-xs font-medium text-blue-800 mb-2 flex items-center gap-1.5">
                      <Users className="w-3.5 h-3.5" /> Admin Account
                    </p>
                    <p className="text-[10px] text-blue-600 leading-relaxed">
                      You are setting up the admin for this organization. After registration, you can invite team members from the Members page.
                    </p>
                  </div>
                )}

                <div>
                  <Label htmlFor="name" className="text-xs font-medium text-slate-700 uppercase tracking-wide">
                    {accountType === 'organization' ? 'Admin Full Name *' : 'Full Name *'}
                  </Label>
                  <Input id="name" value={name} onChange={e => setName(e.target.value)} placeholder="John Doe" className="mt-1 rounded-sm" required data-testid="register-name-input" />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs font-medium text-slate-700 uppercase tracking-wide">Company</Label>
                    <Input value={company} onChange={e => setCompany(e.target.value)} placeholder="Optional" className="mt-1 rounded-sm" data-testid="register-company-input" />
                  </div>
                  <div>
                    <Label className="text-xs font-medium text-slate-700 uppercase tracking-wide">Mobile Phone</Label>
                    <Input value={phone} onChange={e => setPhone(e.target.value)} placeholder="Optional (for SMS)" className="mt-1 rounded-sm" data-testid="register-phone-input" />
                  </div>
                </div>

                {/*
                  A2P 10DLC opt-in.  Always visible (never conditional on phone) so a
                  Twilio reviewer who lands on /register without typing anything still
                  sees the full consent disclosure.  Explicit unchecked checkbox per
                  CTIA Short Code Monitoring + carrier requirements.
                */}
                <div
                  className="bg-blue-50 border border-blue-200 rounded-sm p-4"
                  data-testid="register-sms-consent"
                >
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={smsConsent}
                      onChange={(e) => setSmsConsent(e.target.checked)}
                      className="mt-0.5 w-4 h-4 flex-shrink-0 accent-[#0047AB] cursor-pointer"
                      data-testid="register-sms-consent-checkbox"
                    />
                    <span className="text-[11px] text-blue-900 leading-relaxed">
                      <strong>Yes, send me SMS messages from Real Leads CRM</strong> at the phone
                      number above. I agree to receive automated account, security,
                      transactional (appointment reminders, lead alerts), and 2FA messages from
                      Real Leads CRM. Consent is not a condition of purchase. Message frequency
                      varies. Msg &amp; data rates may apply. Reply HELP for help, STOP to
                      cancel. No mobile information will be shared with third parties or
                      affiliates for marketing purposes. See our{' '}
                      <Link to="/sms-terms" className="underline font-medium" data-testid="register-sms-terms-link" target="_blank" rel="noopener">SMS Terms</Link>
                      {' '}and{' '}
                      <Link to="/privacy" className="underline font-medium" data-testid="register-privacy-link" target="_blank" rel="noopener">Privacy Policy</Link>.
                    </span>
                  </label>
                </div>

                <div>
                  <Label htmlFor="email" className="text-xs font-medium text-slate-700 uppercase tracking-wide">Email *</Label>
                  <Input id="email" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@company.com" className="mt-1 rounded-sm" required data-testid="register-email-input" />
                </div>

                <div>
                  <Label htmlFor="password" className="text-xs font-medium text-slate-700 uppercase tracking-wide">Password *</Label>
                  <Input id="password" type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Min. 6 characters" className="mt-1 rounded-sm" required data-testid="register-password-input" />
                  <PasswordStrength password={password} />
                </div>

                {accountType === 'organization' && (
                  <div>
                    <Label className="text-xs font-medium text-slate-700 uppercase tracking-wide">How many members are you setting up?</Label>
                    <Input
                      type="number"
                      min="1"
                      max="499"
                      value={memberCount}
                      onChange={e => setMemberCount(Math.max(1, parseInt(e.target.value, 10) || 1))}
                      className="mt-1 rounded-sm w-32"
                      data-testid="register-member-count-input"
                    />
                    {(() => {
                      const totalSeats = memberCount + 1;
                      const annual = seatPreset?.cycle === 'annual';
                      // Tiered rate matches SeatCalculator (Solo $35, Team $30, Scale $25; annual /seat/yr 350/300/250).
                      const rate = annual
                        ? (totalSeats >= 6 ? 250 : totalSeats >= 2 ? 300 : 350)
                        : (totalSeats >= 6 ? 25 : totalSeats >= 2 ? 30 : 35);
                      const total = totalSeats * rate;
                      return (
                        <p className="text-[10px] text-slate-400 mt-1.5" data-testid="register-member-count-hint">
                          {totalSeats} total seats (1 admin + {memberCount} member{memberCount !== 1 ? 's' : ''}) &times; ${rate}/seat
                          {annual ? '/yr' : '/mo'} = <strong className="text-slate-700">${total.toLocaleString()}{annual ? '/year' : '/month'}</strong>
                        </p>
                      );
                    })()}
                  </div>
                )}

                {accountType === 'single' && (
                  <div className="bg-emerald-50 border border-emerald-200 rounded-sm p-3">
                    <p className="text-xs text-emerald-700">
                      You&apos;ll be set up as an <strong>Admin</strong> with full access to all CRM features.{' '}
                      {seatPreset?.cycle === 'annual'
                        ? '$350/year'
                        : '$35/month'}{' '}
                      after your 7-day trial.
                    </p>
                  </div>
                )}

                <Button type="submit" className="w-full bg-slate-900 hover:bg-slate-800 text-white rounded-sm h-10 text-sm font-medium" disabled={loading} data-testid="register-submit-btn">
                  {loading ? 'Sending code...' : 'Continue'}
                  {!loading && <ArrowRight className="w-4 h-4 ml-1.5" />}
                </Button>
              </form>

              <p className="text-sm text-slate-500 text-center mt-6">
                Already have an account? <Link to="/login" className="text-[#0047AB] font-medium hover:underline">Sign in</Link>
              </p>
            </>
          )}

          {/* ── STEP 3: Email Verification ── */}
          {step === 'verify' && (
            <>
              <button onClick={() => { setStep('form'); setCode(''); setError(''); }} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 mb-6 transition-colors" data-testid="back-to-form-btn">
                <ArrowLeft className="w-4 h-4" /> Back
              </button>
              <h1 className="font-heading text-2xl font-bold text-slate-900 tracking-tight mb-1">Verify your email</h1>
              <p className="text-sm text-slate-500 mb-6">
                We sent a 6-digit code to <strong className="text-slate-700">{email}</strong>
              </p>
              {error && (
                <div className="flex items-center gap-2 p-3 mb-4 bg-red-50 border border-red-200 rounded-sm text-sm text-red-700" data-testid="verify-error">
                  <AlertCircle className="w-4 h-4 shrink-0" /> {error}
                </div>
              )}
              <form onSubmit={handleVerify} className="space-y-4">
                <div>
                  <Label className="text-xs font-medium text-slate-700 uppercase tracking-wide">Verification Code</Label>
                  <Input
                    value={code} onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="Enter 6-digit code"
                    className="mt-1 rounded-sm text-center text-lg tracking-[0.3em] font-mono"
                    maxLength={6}
                    autoFocus
                    data-testid="verify-code-input"
                  />
                </div>
                <Button type="submit" className="w-full bg-slate-900 hover:bg-slate-800 text-white rounded-sm h-10 text-sm font-medium" disabled={verifying || code.length !== 6} data-testid="verify-submit-btn">
                  {verifying ? 'Verifying...' : 'Verify & Create Account'}
                </Button>
              </form>
              <p className="text-xs text-slate-400 text-center mt-4">
                Didn't receive a code? <button onClick={handleResend} disabled={resending} className="text-[#0047AB] hover:underline" data-testid="resend-code-btn">{resending ? 'Sending...' : 'Resend'}</button>
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
